import React from 'react';
import { seasonNames } from 'resources/common-values';
import { Immutable } from 'nuclear-js';
import HelpBox from 'components/generic/help/index';
import PropTypes from 'prop-types';
import CreateReactClass from 'create-react-class';
import DropdownMenu from 'components/generic/controls/dropdown-menu';
import ConditionalGrowth from './growth-and-achievement-components/conditional-growth';
import QuadrantChart from './growth-and-achievement-components/quadrant-chart/quadrant-chart';
import NormsPercentiles from './growth-and-achievement-components/norms-percentiles';
import ModuleDescription from './growth-and-achievement-components/description';
import CommonStrings from 'resources/common-strings';
import CommonValues from 'resources/common-values';
import RuntimeActions from 'data/app-runtime/actions';

export default CreateReactClass({
    displayName: 'ComparisonsExpandedGrowth',

    propTypes: {
        selectedSubject: PropTypes.instanceOf(Immutable.Map),
        studentBid: PropTypes.string.isRequired,
        subjectBid: PropTypes.number.isRequired,
        studentFirstName: PropTypes.string.isRequired,
        studentGender: PropTypes.string.isRequired,
        comparisonDropdownPeriods: PropTypes.instanceOf(Immutable.Map),
        defaultComparisonPeriodFunction: PropTypes.func,
        selectedComparisonPeriod: PropTypes.instanceOf(Immutable.Map),
        isUserNormData: PropTypes.bool,
        batchInfo: PropTypes.object /* Only exists when in Batch Print mode, used to determine chart rotation */,
    },

    getInitialState() {
        const defaultPeriod = this.props.defaultComparisonPeriodFunction(this.props.comparisonDropdownPeriods);
        return {
            selectedTermRange: defaultPeriod ? defaultPeriod.get('displayRange') : null,
            selectedPeriod: defaultPeriod ? defaultPeriod : null,
        };
    },

    getSelectedDropdownPeriodValue() {
        if (this.props.selectedComparisonPeriod) {
            return this.props.selectedComparisonPeriod.get('displayRange');
        } else {
            let defaultOption = this.props.defaultComparisonPeriodFunction(this.props.comparisonDropdownPeriods);
            if (defaultOption) {
                return defaultOption.get('displayRange');
            }
        }
        return null;
    },

    hasDropdownPeriods() {
        return (
            this.props.comparisonDropdownPeriods &&
            this.props.comparisonDropdownPeriods.get('growthPeriods') &&
            this.props.comparisonDropdownPeriods.get('growthPeriods').size > 0
        );
    },

    changeComparisonPeriod(selectedTermRange) {
        if (this.hasDropdownPeriods()) {
            const userSelection = this.props.comparisonDropdownPeriods
                .get('growthPeriods')
                .find((period) => period.get('displayRange') === selectedTermRange, null);
            RuntimeActions.selectComparisonPeriod(this.props.subjectBid, userSelection);
        }
    },

    renderComparisonPeriodDropdown(subjectName) {
        if (this.hasDropdownPeriods()) {
            return (
                <DropdownMenu
                    id={`comparison-period-dropdown-${subjectName}-${this.props.studentBid}`}
                    key="comparison-period-dropdown"
                    selectedOptionValue={this.getSelectedDropdownPeriodValue()}
                    onChange={this.changeComparisonPeriod}
                    label={CommonStrings.comparisonsGrowthDropdownLabel}
                    optionList={this.props.comparisonDropdownPeriods.get('growthPeriods')}
                    elementValueFunction={(period) => {
                        return period.get('displayRange');
                    }}
                    elementDisplayFunction={(period) => {
                        return period.get('displayRange');
                    }}
                    classesDivWrapper="dropdown comparisons-periods"
                />
            );
        } else {
            const errorAlert = require('./images/errorAlert.svg');
            return (
                <div
                    className="no-comparison-periods"
                    id={`no-comparison-periods-message-${subjectName}-${this.props.studentBid}`}
                >
                    <span>
                        <img className="error-alert" key="error-alert" src={errorAlert} />
                        {CommonStrings.comparisonsGrowthPreviousGrowthEventRequired}
                    </span>
                </div>
            );
        }
    },

    renderConditionalGrowthModule(conditionalGrowthIndex, predictedGrowth, observedGrowth) {
        return (
            <ConditionalGrowth
                subjectBid={this.props.subjectBid}
                studentBid={this.props.studentBid}
                studentFirstName={this.props.studentFirstName}
                studentGender={this.props.studentGender}
                conditionalGrowthIndex={conditionalGrowthIndex}
                predictedGrowth={predictedGrowth}
                observedGrowth={observedGrowth}
            />
        );
    },

    renderNormsPercentilesModule(growthPercentile, achievementPercentile) {
        return (
            <NormsPercentiles
                subjectBid={this.props.subjectBid}
                studentBid={this.props.studentBid}
                growthPercentile={growthPercentile}
                achievementPercentile={achievementPercentile}
                isUserNormData={this.props.isUserNormData}
            />
        );
    },

    helpText() {
        return (
            <div className="comparisons-expanded-help-content" key="comparisons-expanded-help-quadrant-chart">
                <p key="initial-text"> {CommonStrings.quadrantChartHelp.initialText} </p>
            </div>
        );
    },

    renderQuadrantChartModule(growthPercentile, achievementPercentile) {
        const subjectName = CommonValues.getSubjectFieldforBid(this.props.subjectBid, 'shortName');
        const id = `comparisons-growth-and-achievement-expanded-${subjectName}-${this.props.studentBid}-quadrant-chart`;
        const printId = `comparisons-growth-and-achievement-expanded-${subjectName}-${this.props.studentBid}-quadrant-chart-print`;

        return (
            <div
                className="growth-and-achievement-expanded-module quadrant-chart"
                id={`comparisons-growth-and-achievement-expanded-${subjectName}-${this.props.studentBid}-heading`}
                style={{ width: '32%' }}
            >
                <h3 className="comparisons-expanded-module-heading">
                    Quadrant Chart
                    <HelpBox
                        id={`comparisons-expanded-help-box-quadrant-chart-${subjectName}-${this.props.studentBid}`}
                        appLevelCssClassName=""
                        helpContentsRenderer={this.helpText}
                        moduleName={CommonValues.SectionIDs.comparisons}
                        helpLabel="comparisons-view-quadrant-chart-help"
                    />
                </h3>
                <QuadrantChart
                    selectedSubject={this.props.selectedSubject}
                    subjectBid={this.props.subjectBid}
                    chartContainerId={id}
                    studentBid={this.props.studentBid}
                    growthPercentile={growthPercentile}
                    achievementPercentile={achievementPercentile}
                    orientation={CommonValues.QUADRANT_CHART_DEFAULT_ORIENTATION}
                />
            </div>
        );
    },

    renderDescription(growthPercentile, achievementPercentile) {
        return (
            <ModuleDescription
                subjectBid={this.props.subjectBid}
                studentBid={this.props.studentBid}
                studentFirstName={this.props.studentFirstName}
                studentGender={this.props.studentGender}
                growthPercentile={growthPercentile}
                achievementPercentile={achievementPercentile}
            />
        );
    },

    getDataForModule() {
        const defaultPeriod = this.props.defaultComparisonPeriodFunction(this.props.comparisonDropdownPeriods);

        const getInitialValue = (value) => {
            return this.props.selectedComparisonPeriod
                ? this.props.selectedComparisonPeriod.get(value)
                : defaultPeriod
                ? defaultPeriod.get(value)
                : null;
        };

        const growthPercentile = getInitialValue('growthPercentile');
        const conditionalGrowthIndex = getInitialValue('conditionalGrowthIndex');
        const predictedGrowth = getInitialValue('predictedGrowth');
        const observedGrowth = getInitialValue('observedGrowth');
        const achievementPercentile = this.props.comparisonDropdownPeriods
            ? this.props.comparisonDropdownPeriods.get('achievementPercentile')
            : null;

        return { growthPercentile, achievementPercentile, conditionalGrowthIndex, predictedGrowth, observedGrowth };
    },

    render() {
        const subjectName = CommonValues.getSubjectFieldforBid(this.props.subjectBid, 'shortName');
        const {
            growthPercentile,
            achievementPercentile,
            conditionalGrowthIndex,
            predictedGrowth,
            observedGrowth,
        } = this.getDataForModule();
        return (
            <div
                id={`comparisons-growth-expanded-${subjectName}-${this.props.studentBid}`}
                className={'comparisons-expanded-growth'}
            >
                <header className="module-header">
                    <h3 data-testid="growth-achievement-measures-title">
                        GROWTH & ACHIEVEMENT MEASURES
                        <span className="comparisons-expanded-heading-additional">
                            {this.renderComparisonPeriodDropdown(subjectName)}
                        </span>
                    </h3>
                </header>
                <div className="comparisons-expanded-module-content">
                    {this.renderDescription(growthPercentile, achievementPercentile)}
                    <div
                        className="growth-and-achievement-modules-container"
                        style={{ width: '100%', display: 'flex' }}
                    >
                        {this.renderNormsPercentilesModule(growthPercentile, achievementPercentile)}
                        {this.renderQuadrantChartModule(growthPercentile, achievementPercentile)}
                        {this.renderConditionalGrowthModule(conditionalGrowthIndex, predictedGrowth, observedGrowth)}
                    </div>
                </div>
            </div>
        );
    },
});
